import { VIEWS } from "@miplatform/constants";
import { formatDate, getDate, getDaysBetween } from "@miplatform/utils";
import { getTimeSlot } from "./index";

function getAvailableTimeSlots(column, dayStart, dayEnd) {
  const start = parseInt(dayStart.split(":")[0], 10);
  const end = parseInt(dayEnd.split(":")[0], 10);
  const occupiedSlots = column.map(getTimeSlot);
  const sorted = [...occupiedSlots].sort((a, b) => a[0] - b[0]);
  const merged = [];
  for (let i = 0; i < sorted.length; i++) {
    const slot = sorted[i];
    if (!merged.length || slot[0] > merged[merged.length - 1][1]) {
      merged.push([slot[0], slot[1]]);
    } else {
      merged[merged.length - 1][1] = Math.max(
        merged[merged.length - 1][1],
        slot[1]
      );
    }
  }
  const available = [];
  let prevEnd = start;
  for (let i = 0; i < merged.length; i++) {
    const slotStart = merged[i][0];
    const slotEnd = merged[i][1];
    if (slotStart > prevEnd) {
      available.push([prevEnd, slotStart]);
    }
    prevEnd = Math.max(prevEnd, slotEnd);
  }
  if (prevEnd < end) {
    available.push([prevEnd, end]);
  }
  return available;
}

const getExistingOrderTemplateCountPerId = (column) => {
  // Returns a map: { [dateString]: { [templateId]: count } }
  const countMap = {};
  column.forEach((event) => {
    if (event.isCapacityTracker) return;
    const templateId = event?.assignment?.order?.orderTemplateId;
    const dateStr = formatDate(event.displayStartTime, "YYYY-MM-DD");
    if (!countMap[dateStr]) countMap[dateStr] = {};
    if (!countMap[dateStr][templateId]) countMap[dateStr][templateId] = 0;
    countMap[dateStr][templateId]++;
  });
  return countMap;
};

const fillUserCapacityForColumn = (column, userCapacities, availableSlots) => {
  if (
    !Array.isArray(userCapacities) ||
    !Array.isArray(availableSlots) ||
    !Array.isArray(column)
  )
    return;

  const createBlock = (cap, start, end) => {
    const hour = Math.floor(start);
    const minute = Math.round((start - hour) * 60);
    const endHour = Math.floor(end);
    const endMinute = Math.round((end - endHour) * 60);
    const blockDate = cap.date ? getDate(cap.date) : getDate();
    const startTime = blockDate
      .clone()
      .hour(hour)
      .minute(minute)
      .second(0)
      .millisecond(0);
    const endTime = blockDate
      .clone()
      .hour(endHour)
      .minute(endMinute)
      .second(0)
      .millisecond(0);
    return {
      id: `${cap.id}-hr-${
        cap.date ? cap.date.format("YYYY-MM-DD") : "unknown"
      }-${hour}-${minute}`,
      originalId: cap.id,
      assignedUserId: cap.userId,
      orderTemplateShortName: cap.orderTemplateShortName,
      orderTemplateAbbreviation: cap.orderTemplateAbbreviation,
      isCapacityTracker: true,
      readOnly: true,
      noDrag: true,
      capacityColor: cap.color,
      scheduledStartDate: startTime.toDate(),
      scheduledEndDate: endTime.toDate(),
      displayStartTime: startTime.toDate(),
      displayEndTime: endTime.toDate(),
    };
  };

  let lastFilledSlotEnd = null;
  for (let i = 0; i < userCapacities.length; i++) {
    const cap = userCapacities[i];
    let toFill = cap.remainingAmount;
    if (toFill > 0) {
      const blockSize = cap.durationInHours || 1;
      for (let j = 0; j < availableSlots.length; j++) {
        const [slotStart, slotEnd] = availableSlots[j];
        if (!lastFilledSlotEnd || slotEnd > lastFilledSlotEnd) {
          let current = Math.max(slotStart, lastFilledSlotEnd || slotStart);
          while (current + blockSize <= slotEnd && toFill > 0) {
            const nextBlock = current + blockSize;
            column.push(createBlock(cap, current, nextBlock));
            lastFilledSlotEnd = nextBlock;
            toFill--;
            current = nextBlock;
          }
        }
        if (toFill <= 0) break;
      }
    }
  }
};

export const buildUserCapacitiesData = ({
  displayData,
  groupBy,
  calendarView,
  dateRange,
  date,
  users,
  userCapacities,
  viewConfig,
}) => {
  let days = [];
  if (calendarView === VIEWS.DAY) {
    days = [getDate(date).startOf("day")];
  } else if (calendarView === VIEWS.DAYS) {
    days = getDaysBetween(dateRange.startDate, dateRange.endDate);
  }

  const finalDisplayData = [];
  users.forEach((user) => {
    const column = displayData.filter((event) => event[groupBy] === user.id);
    const existingOrderTemplateCountPerDay =
      getExistingOrderTemplateCountPerId(column);
    days.forEach((dayMoment) => {
      const dayStr = formatDate(dayMoment, "YYYY-MM-DD");
      const dayColumn = column.filter(
        (event) => formatDate(event.displayStartTime, "YYYY-MM-DD") === dayStr
      );
      const availableSlots = getAvailableTimeSlots(
        dayColumn,
        viewConfig.dayStartTime,
        viewConfig.dayEndTime
      );

      const userCaps = (userCapacities[user.id]?.[dayStr] || []).map((cap) => ({
        ...cap,
        date: dayMoment,
        durationInHours: (cap.expectedDuration ?? 60) / 60,
        remainingAmount:
          cap.amount -
          (existingOrderTemplateCountPerDay[dayStr]?.[cap.orderTemplateId] ||
            0),
      }));

      fillUserCapacityForColumn(dayColumn, userCaps, availableSlots);
      finalDisplayData.push(...dayColumn);
    });
  });
  return finalDisplayData;
};
